import { LoadingIndicator } from "@/Icons/loadingIndicator";
import { useCommonEntitiesStore } from "@/States/commonEntities";
import { FetchedWasteTypes } from "@/Utils/api/sanity/types";
import { trpc } from "@/Utils/trpc";
import { useConfig } from "@/api/hooks/useConfig";
import { EmptyView } from "@/components/EmptyView/EmptyView";
import Piechart from "@/components/Piechart";
import { PieChartData } from "@/components/Piechart/Piechart.interface";
import { Card, CardComponentFilterLocation } from "@/components/pageCards/card";
import { ActiveOptions } from "@/components/pageCards/filterSort/FilterSortContext";
import { createTimeFilters } from "@/components/pageCards/filterSort/filterCreators";
import { getTimeFrame } from "@/components/pageCards/filterSort/filterTimeFrame";
import { FilterSort } from "@/components/pageCards/filterSort/types";
import {
  WasteTypeConfig,
  useConfigService,
} from "@/pages/configuration/useConfigService";
import {
  COLORS,
  getNewColor,
} from "@/pages/customer/dashboard/functions/pieChartData";
import { useHomeLoadingState } from "@/pages/home/loadingState";
import { useTrans } from "@/translations";

import { useCallback, useEffect, useMemo, useState } from "react";

import {
  DashboardTileType,
  FilterTimeFrameValue,
} from "server/config-api/index.js";
import { TerminalAggregate } from "server/dwhApi/terminals/types.js";

const preparePieChartData = (
  terminal: TerminalAggregate | undefined,
  wasteTypes: FetchedWasteTypes,
  translationTextUnknown: string,
  translationTextOther: string,
  wasteTypeConfig: WasteTypeConfig[],
  otherColor: string,
): PieChartData[] => {
  if (!terminal) return [];

  let curColors = [...COLORS];

  return (
    terminal?.wasteTypes
      ?.reduce(
        (acc: PieChartData[], dwhWasteType) => {
          // Makes sure that the last element is always the "other" waste type
          const other = acc[acc.length - 1];
          const rest = acc.slice(0, -1);

          const customWasteType = wasteTypeConfig.find(
            (wtc) => wtc.wasteTypeCode === dwhWasteType.code,
          );
          const wasteTypeName =
            customWasteType?.name ||
            wasteTypes.find((wasteType) => wasteType.id === dwhWasteType.code)
              ?.name ||
            `${translationTextUnknown} (${dwhWasteType.code})`;

          const wasteShare =
            dwhWasteType.occurrences / terminal.totalOccurrences;

          if (wasteShare < 0.005) {
            return [
              ...rest,
              {
                ...other,
                value: dwhWasteType.occurrences,
              },
            ];
          }

          const [color, newColors] = getNewColor(curColors);
          curColors = newColors;
          return [
            ...rest,
            {
              id: wasteTypeName,
              label: wasteTypeName,
              value: dwhWasteType.occurrences,
              color: customWasteType?.color || color,
            },
            other,
          ];
        },
        [
          {
            id: "Other",
            label: translationTextOther,
            value: 0,
            color: otherColor,
          },
        ],
      )
      .map((el, i) => ({ ...el, id: `${el.id}-${i}` })) // added to mitigate cases where waste types have the same name
      .filter((el) => el.value !== 0) || []
  );
};

type Props = {
  timeFilter?: FilterTimeFrameValue;
  setTimeFilters?: (
    value: FilterTimeFrameValue,
    type: DashboardTileType,
  ) => void;
};

export const WasteTypeDistributionMWM: React.FC<Props> = ({
  timeFilter,
  setTimeFilters,
}) => {
  const [{ period: [selectedPeriod] = [] }, setActiveOptions] = useState<
    ActiveOptions<"period">
  >({} as any);
  const { wasteTypes } = useCommonEntitiesStore();
  const { t } = useTrans();
  const { config } = useConfig();
  const { wasteTypeConfig, terminalConfig } = useConfigService();
  const { setWasteDistributionLoading } = useHomeLoadingState();

  const defaultPeriod = useMemo(
    () =>
      timeFilter ||
      terminalConfig?.defaultTimeFrame ||
      FilterTimeFrameValue.Last30Days,
    [terminalConfig, timeFilter],
  );

  const filters: FilterSort[] = [
    createTimeFilters({ defaultValue: defaultPeriod }),
  ];

  const selectedTimeParams = useMemo(
    () =>
      getTimeFrame(
        selectedPeriod?.value as FilterTimeFrameValue,
        defaultPeriod,
      ),
    [selectedPeriod, defaultPeriod],
  );

  const {
    data: realEstate,
    isLoading,
    isError,
  } = trpc.dwh.terminals.getAggregates.useQuery(selectedTimeParams, {
    queryKeyHashFn: () =>
      [
        "terminalAggregates",
        selectedTimeParams.startTimeISO,
        selectedTimeParams.endTimeISO,
      ].toString(),
  });

  useEffect(() => {
    setWasteDistributionLoading(isLoading);
  }, [setWasteDistributionLoading, isLoading]);

  const otherColor =
    terminalConfig?.otherColor || config.otherColor || "#B2ABAB";
  const wasteTypeDistributionData = useMemo(
    () =>
      wasteTypes?.length
        ? preparePieChartData(
            realEstate?.terminalAggregates,
            wasteTypes,
            t("common:unknown"),
            t("circularityLabels:otherWasteTypes"),
            wasteTypeConfig,
            otherColor,
          )
        : [],
    [t, wasteTypes, realEstate, wasteTypeConfig, otherColor],
  );

  const chart = useMemo(() => {
    if (isLoading) {
      return (
        <div className="flex h-full min-h-full items-center justify-center">
          <LoadingIndicator />
        </div>
      );
    }

    if (isError) {
      return <EmptyView type="failToFetch" className="min-h-[300px]" />;
    }

    if (!wasteTypeDistributionData.length) {
      return <EmptyView type="noDataForInterval" className="min-h-[300px]" />;
    }

    return (
      <Piechart
        data={wasteTypeDistributionData}
        filterLabel={t("hints:clickToSelectOrUnselectWasteTypes")}
      />
    );
  }, [isLoading, isError, wasteTypeDistributionData, t]);

  const onFilterOptionChange = useCallback(
    (activeOptions: ActiveOptions<any>) => {
      setActiveOptions(activeOptions);

      if (setTimeFilters && activeOptions.period) {
        setTimeFilters(
          activeOptions.period[0].value as FilterTimeFrameValue,
          DashboardTileType.WasteTypeDistribution,
        );
      }
    },
    [setActiveOptions, setTimeFilters],
  );

  return (
    <Card
      title={"circularityLabels:wasteTypeDistribution"}
      filters={filters}
      onFilterOptionChange={onFilterOptionChange}
      filterLocation={CardComponentFilterLocation.RIGHT}
    >
      {chart}
    </Card>
  );
};
